import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { PreAuthNavbar } from '@/components/navigation/PreAuthNavbar';
import { Footer } from '@/components/marketing/Footer';
import { AuthCard } from '@/components/auth/AuthCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authService } from '@/services/authService';
import { toast } from 'sonner';
import { CheckCircle, XCircle } from 'lucide-react';

const resetPasswordSchema = Yup.object({
    newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            'Password must contain at least one uppercase letter, one lowercase letter, and one number'
        )
        .required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Please confirm your password'),
});

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [tokenError, setTokenError] = useState('');

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setTokenError('Invalid or missing reset token. Please request a new password reset.');
        }
    }, [token]);

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: resetPasswordSchema,
        onSubmit: async (values) => {
            if (!token) {
                toast.error('Invalid reset token');
                return;
            }

            setIsLoading(true);
            try {
                await authService.resetPassword({
                    token,
                    newPassword: values.newPassword,
                });
                setIsSuccess(true);
                toast.success('Password reset successfully');
            } catch (error: any) {
                toast.error(error.message || 'Failed to reset password');
            } finally {
                setIsLoading(false);
            }
        },
    });

    if (isSuccess) {
        return (
            <div className="min-h-screen flex flex-col">
                <PreAuthNavbar />
                <main className="flex-1 flex items-center justify-center px-4 py-12">
                    <AuthCard
                        title="Password Reset Complete"
                        description="Your password has been successfully updated."
                    >
                        <div className="space-y-4">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full">
                                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                            </div>

                            <div className="text-center space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    You can now sign in with your new password.
                                </p>
                            </div>

                            <Button
                                onClick={() => navigate('/login')}
                                className="w-full"
                            >
                                Continue to Login
                            </Button>
                        </div>
                    </AuthCard>
                </main>
                <Footer />
            </div>
        );
    }

    if (tokenError) {
        return (
            <div className="min-h-screen flex flex-col">
                <PreAuthNavbar />
                <main className="flex-1 flex items-center justify-center px-4 py-12">
                    <AuthCard
                        title="Invalid Reset Link"
                        description="This password reset link is invalid or has expired."
                    >
                        <div className="space-y-4">
                            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full">
                                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                            </div>

                            <div className="text-center space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    {tokenError}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Button
                                    onClick={() => navigate('/forgot-password')}
                                    className="w-full"
                                >
                                    Request New Reset Link
                                </Button>
                                <div className="text-center">
                                    <Link
                                        to="/login"
                                        className="text-sm text-primary hover:underline"
                                    >
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </AuthCard>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <PreAuthNavbar />
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <AuthCard
                    title="Set New Password"
                    description="Enter your new password below."
                >
                    <form onSubmit={formik.handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <Input
                                id="newPassword"
                                name="newPassword"
                                type="password"
                                placeholder="Enter your new password"
                                value={formik.values.newPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.newPassword && formik.errors.newPassword ? 'border-destructive' : ''}
                                autoComplete="new-password"
                            />
                            {formik.touched.newPassword && formik.errors.newPassword && (
                                <p className="text-sm text-destructive">{formik.errors.newPassword}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                placeholder="Confirm your new password"
                                value={formik.values.confirmPassword}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-destructive' : ''}
                                autoComplete="new-password"
                            />
                            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                <p className="text-sm text-destructive">{formik.errors.confirmPassword}</p>
                            )}
                        </div>

                        <div className="bg-muted/50 rounded-lg p-4">
                            <h4 className="text-sm font-medium mb-2">Password Requirements:</h4>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• At least 8 characters long</li>
                                <li>• Contains at least one uppercase letter</li>
                                <li>• Contains at least one lowercase letter</li>
                                <li>• Contains at least one number</li>
                            </ul>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading || !formik.isValid || !formik.dirty}
                            className="w-full"
                        >
                            {isLoading ? 'Updating...' : 'Update Password'}
                        </Button>

                        <div className="text-center">
                            <Link
                                to="/login"
                                className="text-sm text-primary hover:underline"
                            >
                                ← Back to Login
                            </Link>
                        </div>
                    </form>
                </AuthCard>
            </main>
            <Footer />
        </div>
    );
}
